// src/pages/dashboard/components/AccionesPendientes/index.js
import { useState, useEffect } from 'react';
import { Grid, Card, CardActionArea, Typography, Skeleton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { invoicesApi, librosApi } from '../../../../services/api';
import { formatearMoneda } from '../../../../utils/currency';

/**
 * Lo que hay que hacer, arriba de todo.
 *
 * El resto del panel cuenta cómo fue el mes: cuánto se facturó, cuántos
 * clientes hay, el calendario. Todo eso es historia, y está bien que esté, pero
 * no le dice a nadie qué hacer al abrir el sistema a la mañana.
 *
 * Estas tres tarjetas sí. Las tres se calculan con datos que el sistema ya
 * tiene y que hasta ahora había que ir a buscar a tres pantallas distintas:
 *
 *  - Cuánto le deben, que es lo primero que mira cualquiera que factura.
 *  - Cuántos borradores quedaron sin emitir, que es plata que todavía no es plata.
 *  - Cuánto IVA va a pagar este mes. Este es el que ningún sistema genérico
 *    muestra, y sale del mismo cálculo que arma el Libro de Ventas.
 *
 * Cada una lleva a la pantalla donde se resuelve: son un punto de partida, no
 * un adorno informativo.
 */

const mesActual = () => {
  const hoy = new Date();
  const anio = hoy.getFullYear();
  const mes = String(hoy.getMonth() + 1).padStart(2, '0');
  const ultimo = new Date(anio, hoy.getMonth() + 1, 0).getDate();
  return { desde: `${anio}-${mes}-01`, hasta: `${anio}-${mes}-${ultimo}` };
};

/** Facturas que todavía no se cobraron. */
const PENDIENTES_DE_COBRO = ['pending', 'partial', 'overdue'];

/**
 * Cada tarjeta es un enlace de verdad, no un div que responde al clic.
 *
 * Un div con onClick funciona con el mouse y con nada más: no se alcanza con el
 * tabulador, no se puede abrir en otra pestaña, y un lector de pantalla no lo
 * anuncia como algo a lo que se pueda ir. Como el destino es una pantalla
 * concreta, corresponde un enlace.
 */
const Tarjeta = ({ titulo, valor, detalle, alerta, cargando, a }) => (
  <Card sx={{ height: '100%' }}>
    <CardActionArea
      component={RouterLink}
      to={a}
      sx={{
        display: 'block',
        p: 2.25,
        height: '100%',
        transition: 'background-color .16s ease',
        '&:hover': { bgcolor: 'rgba(245, 166, 35, 0.05)' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 }
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.6875rem', letterSpacing: '0.08em' }}
      >
        {titulo}
      </Typography>

      {cargando ? (
        <Skeleton width="70%" height={34} sx={{ mt: 0.5 }} />
      ) : (
        <Typography
          variant="h4"
          sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums', color: alerta ? 'error.main' : 'text.primary' }}
        >
          {valor}
        </Typography>
      )}

      <Typography variant="body2" sx={{ mt: 0.25, color: alerta ? 'error.main' : 'text.secondary' }}>
        {cargando ? '\u00a0' : detalle}
      </Typography>
    </CardActionArea>
  </Card>
);

export const AccionesPendientes = ({ moneda = 'VES' }) => {
  const [datos, setDatos] = useState(null);

  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const [facturas, resumen] = await Promise.all([
          invoicesApi.getAll(),
          // Si los libros fallan, las otras dos tarjetas tienen que salir igual.
          librosApi.resumen(mesActual()).catch(() => null)
        ]);

        const lista = Array.isArray(facturas) ? facturas : (facturas?.invoices || []);
        const porCobrar = lista.filter((f) => PENDIENTES_DE_COBRO.includes(f.status));
        const hoy = new Date().toISOString().slice(0, 10);
        const vencidas = porCobrar.filter((f) => f.dueDate && String(f.dueDate).slice(0, 10) < hoy);

        if (!vigente) return;
        setDatos({
          porCobrar: porCobrar.reduce((s, f) => s + (Number(f.total) || 0), 0),
          cantidadPorCobrar: porCobrar.length,
          vencidas: vencidas.length,
          borradores: lista.filter((f) => f.status === 'draft').length,
          iva: resumen ? resumen.aPagar : null,
          excedente: resumen ? resumen.excedenteASiguienteMes : null
        });
      } catch {
        if (vigente) setDatos({ error: true });
      }
    })();
    return () => { vigente = false; };
  }, []);

  const cargando = !datos;
  const d = datos || {};

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} md={4}>
        <Tarjeta
          titulo="Por cobrar"
          valor={formatearMoneda(d.porCobrar || 0, moneda)}
          detalle={
            d.vencidas
              ? `${d.cantidadPorCobrar} facturas · ${d.vencidas} vencida${d.vencidas === 1 ? '' : 's'}`
              : `${d.cantidadPorCobrar || 0} factura${d.cantidadPorCobrar === 1 ? '' : 's'} sin cobrar`
          }
          alerta={d.vencidas > 0}
          cargando={cargando}
          a="/invoices"
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Tarjeta
          titulo="Sin emitir"
          valor={d.borradores || 0}
          detalle={d.borradores ? 'Borradores esperando emisión' : 'No hay borradores pendientes'}
          cargando={cargando}
          a="/invoices"
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Tarjeta
          titulo="IVA de este mes"
          valor={d.iva !== null && d.iva !== undefined
            ? formatearMoneda(d.excedente > 0 ? d.excedente : d.iva, 'VES')
            : '—'}
          detalle={d.excedente > 0
            ? 'A favor, se arrastra al mes que viene'
            : 'Débito menos crédito fiscal'}
          cargando={cargando}
          a="/libros"
        />
      </Grid>
    </Grid>
  );
};

export default AccionesPendientes;
